import { Box, Typography } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import DraggableWindow from '../../../../../../components/draggable-window'
import Icon from '../../../../../../components/icon'
import { formatDateTime } from '../../../../../../utils/utils'
import { modalStyle } from './timelineUtils'

const AuxiliaryDeviceEvidence = ({
  evidences,
  startTime,
  position,
  isMinimised,
  showOnTop,
  onMinimiseChange,
  draggableProps
}) => {
  // State
  const [expanded, setExpanded] = useState(false)
  const [currentImage, setCurrentImage] = useState(null)

  // Variables
  const sortedData = useMemo(() => {
    return evidences
      ?.map((item) => ({
        ...item,
        timestamp: item?.fileName?.split('.')?.[0]?.split('_')?.[1]
      }))
      .sort((a, b) => {
        return new Date(a?.timestamp) - new Date(b?.timestamp)
      })
  }, [evidences])

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    const date = timestamp ? new Date(Number(timestamp)) : null
    return formatDateTime(date, false, false, true)
  }

  // Sync image with timeline position
  const syncImageWithTimeline = useCallback(() => {
    if (startTime && sortedData?.length > 0) {
      const localDate = new Date(startTime)
      localDate.setSeconds(localDate.getSeconds() + position)

      // Find the latest image before or at the current timeline position
      const validImages = sortedData.filter((item) => {
        const imageDate = new Date(Number(item.timestamp))
        return imageDate <= localDate
      })

      if (validImages.length > 0) {
        const latestImage = validImages[validImages.length - 1]
        setCurrentImage(latestImage)
      } else {
        setCurrentImage(null)
      }
    } else {
      setCurrentImage(null)
    }
  }, [startTime, position, sortedData])

  // Effect to sync image when timeline position changes
  useEffect(() => {
    syncImageWithTimeline()
  }, [syncImageWithTimeline])

  // Don't render if minimised or no data
  if (isMinimised || sortedData?.length === 0) {
    return null
  }

  const streamContainerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: 'var(--background-paper)',
    borderRadius: '8px',
    overflow: 'hidden'
  }

  return (
    <DraggableWindow
      className="auxiliary-device-window"
      offsetY={250}
      offsetX={10}
      modalStyle={{
        ...modalStyle,
        ...(showOnTop ? { zIndex: '2001 !important' } : {})
      }}
      zoomed={expanded}
      draggableProps={draggableProps ? { ...draggableProps } : {}}
    >
      <Box sx={streamContainerStyle}>
        <Box
          className="minimise-container"
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            gap: '8px',
            zIndex: 2
          }}
        >
          <Icon
            hoverEffect
            title="Resize screen"
            icon={expanded ? 'mdi:zoom-out' : 'mdi:zoom-in'}
            hoverBoxStyle={{ zIndex: 2 }}
            style={{ color: 'var(--common-white)', zIndex: 2 }}
            onClick={() => setExpanded((prev) => !prev)}
          />
          <Icon
            className="minimise-icon"
            hoverEffect
            title="Minimize"
            icon={'lsicon:minus-outline'}
            style={{ color: 'var(--common-white)', zIndex: 2 }}
            hoverBoxStyle={{ zIndex: 2 }}
            onClick={() => onMinimiseChange?.(true)}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
            backgroundColor: '#000',
            position: 'relative',
            maxHeight: '100%'
          }}
        >
          {currentImage ? (
            <>
              <img
                src={currentImage.url}
                alt={currentImage.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(currentImage.url, '_blank')}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  padding: 1,
                  borderRadius: 1
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--common-white)' }}
                >
                  {formatTimestamp(currentImage.timestamp)}
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Icon
                icon="streamline:mobile-phone-camera-remix"
                style={{ fontSize: '48px', color: 'var(--common-white)' }}
              />
              <Typography variant="body2" sx={{ color: 'var(--common-white)' }}>
                No evidence at this time
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </DraggableWindow>
  )
}

export default memo(AuxiliaryDeviceEvidence)
